import PropTypes from 'prop-types';

import {Flex, Box, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

/**
 * Split Heading (standalone block)
 *
 * Content type: headingLeftSize (Large/Medium), textLeft (preferred) or title, textRight. Figma Desktop 3132-4934, Mobile 3132-6405 (KL-Designs).
 * Left: Large = Section Heading 40 (40/28px), Medium = smaller. Right: text.body.large (18/16px). Layout: row desktop, column mobile.
 */
export const SplitHeading = ({component, isComplete = true, isFetching = false, ...props}) => {
  const {headingLeftSize, textLeft, textRight, title} = component ?? {};
  const displayLeft = textLeft ?? title;
  const variant = headingLeftSize?.toLowerCase() || 'large';
  const styles = useMultiStyleConfig('contentful/split-heading', {
    component,
    isComplete,
    isFetching,
    variant,
  });

  const trimmedRight = typeof textRight === 'string' ? textRight.trim() : textRight;
  const hasLeft = Boolean(displayLeft);
  const hasRightContent = Boolean(trimmedRight);
  const isRightBlank = !hasRightContent;

  if (!hasLeft && !hasRightContent) {
    return null;
  }

  const {className, ...restProps} = props;
  const mergedClassName = [
    className,
    isRightBlank ? 'split-heading--right-blank' : null,
  ]
    .filter(Boolean)
    .join(' ');

  const showRightColumn = hasLeft || hasRightContent;
  const {className: rightRegionThemeClassName, ...rightRegionStyles} = styles.rightRegion ?? {};
  const rightRegionClassName = [
    rightRegionThemeClassName,
    isRightBlank ? 'split-heading__right-region--blank' : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Flex {...styles.container} {...restProps} className={mergedClassName}>
      {hasLeft && (
        <Box {...styles.leftRegion}>
          <Text {...styles.headingLeft} as={variant === 'medium' ? 'h2' : 'h1'}>
            {displayLeft}
          </Text>
        </Box>
      )}
      {showRightColumn && (
        <Box
          {...rightRegionStyles}
          className={rightRegionClassName}
          aria-hidden={isRightBlank && !hasRightContent ? true : undefined}
        >
          {hasRightContent && (
            <Text {...styles.textRight}>{trimmedRight}</Text>
          )}
        </Box>
      )}
    </Flex>
  );
};

SplitHeading.displayName = 'SplitHeading';
SplitHeading.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
